import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppProvider, useApp } from "./context/AppContext";
import { LangProvider } from "./lib/i18n";
import { I18nAuto } from "./lib/i18nAuto";
import { Toaster } from "./components/ui/sonner";

import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Analyzing from "./pages/Analyzing";
import Dashboard from "./pages/Dashboard";
import CommandCenter from "./pages/CommandCenter";
import ContentStudio from "./pages/ContentStudio";
import CalendarPage from "./pages/CalendarPage";
import EventLab from "./pages/EventLab";
import Reputation from "./pages/Reputation";
import Loyalty from "./pages/Loyalty";
import GrowthSimulator from "./pages/GrowthSimulator";
import Profile from "./pages/Profile";
import HelpCenter from "./pages/HelpCenter";
import DemoPicker from "./pages/DemoPicker";
import DemoStory from "./pages/DemoStory";
import PlanStudio from "./pages/PlanStudio";
import CompanyProfile360 from "./pages/CompanyProfile360";
import DocumentAnalysis from "./pages/DocumentAnalysis";
import Diagnostic360 from "./pages/Diagnostic360";
import Legal from "./pages/Legal";
import Home from "./pages/Home";
import StartGuide from "./pages/StartGuide";

function FullLoader() {
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-neon" size={32} /></div>;
}

function Protected({ children }) {
  const { user, demo, checking } = useApp();
  if (checking) return <FullLoader />;
  if (!user && !demo) return <Navigate to="/login" replace />;
  return children;
}

function HomeSwitch() {
  const { demo } = useApp();
  return demo ? <Dashboard /> : <Home />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/" element={<Landing />} />
      <Route path="/demo" element={<DemoPicker />} />
      <Route path="/demo/story" element={<Protected><DemoStory /></Protected>} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot" element={<AuthPage mode="forgot" />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/analyzing" element={<Analyzing />} />
      <Route path="/app/dashboard" element={<Protected><HomeSwitch /></Protected>} />
      <Route path="/app/results" element={<Protected><Dashboard variant="results" /></Protected>} />
      <Route path="/app/start" element={<Protected><StartGuide /></Protected>} />
      <Route path="/app/command" element={<Protected><CommandCenter /></Protected>} />
      <Route path="/app/plan" element={<Protected><PlanStudio /></Protected>} />
      <Route path="/app/profile-360" element={<Protected><CompanyProfile360 /></Protected>} />
      <Route path="/app/documents" element={<Protected><DocumentAnalysis /></Protected>} />
      <Route path="/app/diagnostic" element={<Protected><Diagnostic360 /></Protected>} />
      <Route path="/app/content" element={<Protected><ContentStudio /></Protected>} />
      <Route path="/app/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/app/event" element={<Protected><EventLab /></Protected>} />
      <Route path="/app/reputation" element={<Protected><Reputation /></Protected>} />
      <Route path="/app/loyalty" element={<Protected><Loyalty /></Protected>} />
      <Route path="/app/growth" element={<Protected><GrowthSimulator /></Protected>} />
      <Route path="/app/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/app/help" element={<Protected><HelpCenter /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AppProvider>
        <BrowserRouter>
          <I18nAuto />
          <AppRouter />
          <Toaster position="top-center" theme="dark" richColors />
        </BrowserRouter>
      </AppProvider>
    </LangProvider>
  );
}
